/* Jadlam Paint Picker: "Finish the job".
 * Suggests the primers, thinners, varnishes, brushes etc. that Jadlam customers actually buy with each brand and type of
 * paint (assets/wl-paint-pairings.json, built from 12 months of orders; thinners only ever for the same kind of paint).
 * - One group per brand/type of paint in the basket, skipping anything (or any kind of supply) already in the basket.
 * - A slim "Finish the job" line in the basket tray opens the suggestions; they also open by themselves when the
 *   customer presses View basket (the tray) or Checkout on this page. Either way, at most once per visit.
 * - "Goes well with this paint" in each paint's details.
 * - A promoted product (section setting, e.g. Jadlam's own airbrush cleaning kit) pinned first for airbrush paint.
 * Live price and stock come from /products/<handle>.js. Talks to wl-paint-picker.js only through events on #wlpp:
 * wlpp:basket and wlpp:detail in, wlpp:cart-changed out. */
(function () {
  'use strict';
  const root = document.getElementById('wlpp');
  if (!root) return;
  let CFG = {};
  try { CFG = JSON.parse(document.getElementById('wlpp-config').textContent); } catch (e) {}
  if (!CFG.pairings) return;
  const ROUTE = (window.Shopify && Shopify.routes && Shopify.routes.root) || CFG.root || '/';
  const $ = id => document.getElementById(id);
  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = pence => '£' + (pence / 100).toFixed(2);
  const productUrl = h => `${ROUTE}products/${encodeURIComponent(h)}`;
  const SHOWN = 'wlpp-finish-shown';
  const store = { get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }, set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} } };

  /* ---------- Data ---------- */
  let DATA = null, dataPromise = null;
  const loadData = () => dataPromise || (dataPromise = fetch(CFG.pairings).then(r => { if (!r.ok) throw new Error(r.status); return r.json(); }).then(j => (DATA = j)).catch(e => { dataPromise = null; throw e; }));
  // Live details of a suggested product, fetched only when it is about to be shown.
  const products = new Map(), byVariant = new Map();
  function product(h) {
    if (!products.has(h)) {
      products.set(h, fetch(`${productUrl(h)}.js`).then(r => r.ok ? r.json() : null).then(j => {
        if (!j || !j.variants || !j.variants.length) return null;
        const v = j.variants.find(x => x.available) || j.variants[0];
        let img = j.featured_image || null;
        if (img && img.startsWith('//')) img = 'https:' + img;
        const p = { h, t: j.title, v: String(v.id), price: v.price, was: v.compare_at_price > v.price ? v.compare_at_price : 0, avail: !!v.available, img, variants: j.variants.map(x => String(x.id)) };
        byVariant.set(p.v, p);
        return p;
      }).catch(() => null));
    }
    return products.get(h);
  }

  // Must match the analysis that built the data: brand | kind of paint | format.
  function paintKind(p) {
    const s = `${p.type || ''} ${p.range || ''}`;
    if (/lacquer|mr\.?\s?colou?r/i.test(s)) return 'lacquer';
    if (/enamel/i.test(s)) return 'enamel';
    if (/acryl|aqueous/i.test(s)) return 'acrylic';
    return 'other';
  }
  // Paint made for (or normally put through) an airbrush. Spray cans and markers never count.
  const AIRBRUSH_RANGE = /\bair\b|-air\b|model air|metal colou?r|real colou?rs|kaleido|mr\.? colou?r|mr metallic|super metallic|^sms |tamiya lp/i;
  const isAirbrush = p => AIRBRUSH_RANGE.test(p.range || '') && !/spray|marker/i.test(p.container || '');
  const paintFormat = p => /spray/i.test(p.container || '') ? 'spray' : /marker/i.test(p.container || '') ? 'marker' : 'pot';
  function groupLabel(brand, kind, format) {
    if (format === 'spray') return `${brand} spray paints`;
    if (format === 'marker') return `${brand} paint markers`;
    return kind === 'other' ? `${brand} paints` : `${brand} ${kind} paints`;
  }

  /* ---------- Choosing suggestions ---------- */
  let qty = {}, basketPaints = [];
  // One group per brand/kind/format of paint (most paints first), each with up to `perGroup` suggestions: the most
  // bought product of each of the most bought kinds of supply. A kind of supply already in the basket is skipped.
  async function suggest(paints, perGroup, maxGroups) {
    await loadData();
    const groups = new Map();
    for (const p of paints) {
      const kind = paintKind(p), format = paintFormat(p), key = `${p.brand}|${kind}|${format}`;
      const g = groups.get(key) || { key, brand: p.brand, kind, format, weight: 0, air: false };
      g.weight += qty[p.variantId] || 1;
      g.air = g.air || isAirbrush(p);
      groups.set(key, g);
    }
    const inBasket = new Set(Object.keys(qty).filter(id => qty[id] > 0));
    const used = new Set(), out = [];
    let promoDone = !CFG.promo;
    for (const g of [...groups.values()].sort((a, b) => b.weight - a.weight).slice(0, maxGroups)) {
      // The promoted product goes first in the first group with airbrush paint (unless it is already in the basket).
      let promo = null;
      if (!promoDone && g.air) {
        promoDone = true;
        const p = await product(CFG.promo.handle);
        if (p && p.avail && !p.variants.some(v => inBasket.has(v))) { promo = { role: 'Airbrush cleaning', p, promo: true }; used.add(p.h); }
      }
      // This kind of paint's own picks first, then its brand's, then the store-wide ones, until there are enough:
      // some kinds (Citadel pots) have plenty of orders but no supply bought often enough to count.
      const own = DATA.groups[g.key], roles = new Set(), picks = [];
      for (const d of [own, DATA.brands[g.brand], DATA.all]) {
        for (const pk of (d && d.picks) || []) {
          if (picks.length >= perGroup + 2 || roles.has(pk.r)) continue;
          roles.add(pk.r);
          picks.push({ ...pk, own: d === own });
        }
      }
      const fetched = await Promise.all(picks.map(pk => Promise.all(pk.i.map(it => product(it.h)))));
      const items = [];
      picks.forEach((pk, i) => {
        if (items.length >= perGroup) return;
        const cands = fetched[i];
        if (cands.some(p => p && p.variants.some(v => inBasket.has(v)))) return; // they already have one of these
        const j = cands.findIndex(p => p && p.avail && !used.has(p.h));
        if (j < 0) return;
        used.add(cands[j].h);
        items.push({ role: pk.r, p: cands[j], top: j === 0, own: pk.own });
      });
      if (promo) items.unshift(promo);
      if (items.length) out.push({ label: groupLabel(g.brand, g.kind, g.format), items });
    }
    return out;
  }

  /* ---------- Basket ---------- */
  const pending = new Set();
  const basketSvg = root.wlppBasketSvg || '';
  function buyButton(p) {
    if (!p.avail) return '<span class="wlpp-soldout">Sold out</span>';
    const n = qty[p.v] || 0, busy = pending.has(p.v);
    return `<button type="button" class="wlpp-basket${n ? ' wlpp-basket--added' : ''}${busy ? ' wlpp-basket--busy' : ''}" data-sp-add="${p.v}" title="Add to basket" aria-label="Add ${esc(p.t)} to basket${n ? ', ' + n + ' in basket' : ''}"><span class="wlpp-basket-art wlpp-basket-art--normal">${basketSvg}</span><span class="wlpp-basket-art wlpp-basket-art--hover">${basketSvg}</span>${n ? `<span class="wlpp-basket-count" aria-hidden="true">${n}</span>` : ''}</button>`;
  }
  const buy = p => `<span class="wlpp-buy" data-sp-buy="${p.v}">${buyButton(p)}</span>`;
  function refreshButtons() {
    document.querySelectorAll('[data-sp-buy]').forEach(el => { const p = byVariant.get(el.dataset.spBuy); if (p) el.innerHTML = buyButton(p); });
    renderAddAll();
  }
  /* ---------- Shown / added, for the upsells app's "Recorded activity" ---------- */
  // Same endpoint and format as the app's product page block (/apps/wl-upsells/track, its session id), surface 'paint'
  // to match the _wl_upsell tag on the lines sold here: the paint group as anchor, the suggested variant as candidate,
  // its kind of supply (glue, thinner...) as bucket, its place in the group as position. Shown once the window opens
  // (the tray line only previews a few thumbnails). Fire and forget: never in the way of an add.
  function sid() {
    try {
      let s = sessionStorage.getItem('wlu-sid');
      if (!s) { s = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem('wlu-sid', s); }
      return s;
    } catch (e) { return 'anonymous'; }
  }
  function track(event, rows) {
    const events = rows.filter(r => anchorOf.get(r.v)).map(r => ({ surface: 'paint', event, anchorId: String(anchorOf.get(r.v)).slice(0, 64), candidateId: String(r.v), bucket: String(roleOf.get(r.v) || 'unknown').toLowerCase().slice(0, 32), position: r.pos || 0 }));
    for (let i = 0; i < events.length; i += 12) { // the endpoint takes 12 a call
      try { fetch('/apps/wl-upsells/track', { method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true, body: JSON.stringify({ sessionId: sid(), events: events.slice(i, i + 12) }) }).catch(() => {}); } catch (e) {}
    }
  }

  let chain = Promise.resolve();
  function add(v) {
    const p = byVariant.get(v);
    if (!p || !p.avail || pending.has(v)) return Promise.resolve();
    pending.add(v); refreshButtons();
    return (chain = chain.then(async () => {
      try {
        const res = await fetch(ROUTE + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(v), quantity: 1, properties: Object.assign({ _wl_upsell: 'paint' }, anchorOf.get(v) ? { _wl_anchor: anchorOf.get(v) } : {}) }], sections: 'cart-drawer,cart-icon-bubble', sections_url: location.pathname }) });
        let data = null; try { data = await res.json(); } catch (e) {}
        if (!res.ok || (data && Number(data.status) >= 400)) throw new Error((data && typeof data.description === 'string' && data.description) || 'Sorry, the basket could not be updated. Please try again.');
        qty[v] = (qty[v] || 0) + 1;
        track('added', [{ v, pos: posOf.get(v) }]);
        themeCart(data && data.sections);
        say(`${p.t} added to your basket.`);
      } catch (e) { toast(e.message); say(e.message); }
      finally {
        pending.delete(v); refreshButtons();
        root.dispatchEvent(new Event('wlpp:cart-changed')); // the picker re-reads the basket and updates its tray
      }
    }));
  }
  function themeCart(sections) {
    const drawer = document.querySelector('cart-drawer');
    if (sections && drawer && typeof drawer.renderContents === 'function') { try { drawer.renderContents({ id: null, sections }); } catch (e) {} }
    else if (sections && sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      const fresh = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('#cart-icon-bubble');
      if (bubble && fresh) bubble.innerHTML = fresh.innerHTML;
    }
    if (drawer) drawer.classList.remove('is-empty');
  }
  let toastTimer;
  function toast(msg) { const t = $('wlpp-toast'); if (!t) return; t.textContent = msg; clearTimeout(toastTimer); toastTimer = setTimeout(() => { t.textContent = ''; }, 5000); }
  function say(msg) { const l = $('wlpp-live'); if (!l) return; l.textContent = ''; setTimeout(() => { l.textContent = msg; }, 30); }

  /* ---------- Rendering ---------- */
  // The data's first choice for a kind of supply is the one most often bought with these paints.
  const shareText = (x, label) => x.promo ? (CFG.promo.note || '') : !x.own ? 'Popular with modellers' : x.top ? `Most bought with ${label}` : `Often bought with ${label}`;
  // Which paint group each suggestion was shown for: stamped on the basket line (hidden) with _wl_upsell 'paint', the
  // key the upsells app's sales attribution reads, so Finish the job sales show up there per paint group.
  const anchorOf = new Map(), roleOf = new Map(), posOf = new Map();
  const card = (x, label) => (anchorOf.set(x.p.v, label), roleOf.set(x.p.v, x.role), '') + `<div class="wlpp-pair${x.promo ? ' wlpp-pair--promo' : ''}"><a class="wlpp-pair-img" href="${productUrl(x.p.h)}" tabindex="-1" aria-hidden="true">${x.p.img ? `<img src="${esc(x.p.img)}${x.p.img.includes('?') ? '&' : '?'}width=120" alt="" loading="lazy" width="52" height="52">` : ''}</a><div class="wlpp-pair-id"><small><b>${esc(x.role)}</b>${x.promo ? '<i>Jadlam’s own</i>' : ''}</small><a href="${productUrl(x.p.h)}" title="${esc(x.p.t)}">${esc(x.p.t)}</a><span class="wlpp-pair-why">${esc(shareText(x, label))}</span><span class="wlpp-price">${money(x.p.price)}${x.p.was ? ` <s>${money(x.p.was)}</s>` : ''}</span></div>${buy(x.p)}</div>`;

  let current = [], currentRun = 0, stale = false;
  async function refreshSuggestions() {
    // Keep the list steady while the window is open (e.g. after "Add all"); work it out again once it closes.
    if (finish && finish.open) { stale = true; return; }
    const run = ++currentRun;
    if (!basketPaints.length) { current = []; renderTray(); return; }
    let s = [];
    try { s = await suggest(basketPaints, 3, 3); } catch (e) { s = []; }
    if (run !== currentRun) return;
    current = s; renderTray();
  }
  function measureTray() {
    const tray = $('wlpp-tray');
    if (!tray || tray.hidden) return;
    requestAnimationFrame(() => { const h = tray.offsetHeight + 'px'; root.style.setProperty('--wlpp-tray-h', h); document.body.style.setProperty('--wlpp-tray-h', h); });
  }
  function renderTray() {
    const box = $('wlpp-pair-tray');
    if (!box) return;
    const items = current.flatMap(g => g.items);
    box.hidden = !items.length;
    box.innerHTML = items.length ? `<div class="wlpp-pair-line"><span class="wlpp-pair-thumbs" aria-hidden="true">${items.slice(0, 3).map(x => x.p.img ? `<img src="${esc(x.p.img)}${x.p.img.includes('?') ? '&' : '?'}width=80" alt="" width="28" height="28">` : '').join('')}</span><strong>Finish the job</strong><span>${items.length} thing${items.length === 1 ? '' : 's'} modellers add to orders like yours</span><button type="button" class="wlpp-link" data-sp-open>See suggestions</button></div>` : '';
    measureTray();
  }

  /* ---------- The "Finish the job" window ---------- */
  const finish = $('wlpp-ftj');
  let finishLeaving = null; // the click that was heading for the basket/checkout, carried on when they continue
  function renderFinish() {
    const all = current.flatMap(g => g.items).filter(x => x.p.avail);
    $('wlpp-ftj-body').innerHTML = current.map(g => `<section class="wlpp-finish-group"><h3>For your ${esc(g.label)}</h3><div class="wlpp-finish-list">${g.items.map(x => card(x, g.label)).join('')}</div></section>`).join('');
    $('wlpp-ftj-continue').textContent = !finishLeaving ? 'Done' : toBasket(finishLeaving) ? 'Continue to basket' : 'Continue to checkout';
    renderAddAll(all);
  }
  function renderAddAll(all) {
    const b = $('wlpp-ftj-all');
    if (!b || !finish || !finish.open) return;
    all = all || current.flatMap(g => g.items).filter(x => x.p.avail);
    const todo = all.filter(x => !(qty[x.p.v] > 0));
    b.hidden = !todo.length;
    b.disabled = todo.some(x => pending.has(x.p.v));
    b.textContent = todo.length === all.length ? `Add all ${todo.length} · ${money(todo.reduce((s, x) => s + x.p.price, 0))}` : `Add the other ${todo.length} · ${money(todo.reduce((s, x) => s + x.p.price, 0))}`;
  }
  function openFinish(leaving) {
    store.set(SHOWN, '1'); // seen once (from the tray or on the way to checkout): Checkout then goes straight through
    finishLeaving = leaving || null;
    renderFinish();
    if (!finish.open) { if (finish.showModal) finish.showModal(); else finish.setAttribute('open', ''); }
    renderAddAll();
    current.forEach(g => track('shown', g.items.filter(x => x.p.avail).map((x, i) => (posOf.set(x.p.v, i + 1), { v: x.p.v, pos: i + 1 }))));
  }
  function closeFinish(proceed) {
    const go = proceed && finishLeaving;
    if (finish.open) { if (finish.close) finish.close(); else finish.removeAttribute('open'); }
    finishLeaving = null;
    if (go) go.click(); // carry on to the basket or checkout as they first asked (the window shows once per visit)
  }
  if (finish) {
    finish.addEventListener('click', e => {
      if (e.target === finish) return closeFinish(false);
      const b = e.target.closest('button');
      if (!b) return;
      if (b.dataset.spAdd) add(b.dataset.spAdd);
      else if (b.id === 'wlpp-ftj-all') current.flatMap(g => g.items).filter(x => x.p.avail && !(qty[x.p.v] > 0)).forEach(x => add(x.p.v));
      else if (b.id === 'wlpp-ftj-continue') closeFinish(true);
      else if (b.classList.contains('wlpp-dialog-close')) closeFinish(false);
    });
    finish.addEventListener('cancel', () => { finishLeaving = null; });
    finish.addEventListener('close', () => { if (stale) { stale = false; refreshSuggestions(); } });
  }

  // Leaving the Paint Picker for the basket or checkout, with paints in the basket, shows the suggestions first, once per
  // visit: the tray's View basket, and the slide-out basket's View Basket link and Checkout button. Nothing interrupts
  // picking: adding paints, and the header basket icon (it only opens the slide-out basket on this page), go straight
  // through. Already seen, nothing to suggest, or still working them out: the click goes straight through too.
  function leavesFor(el) {
    if (el.id === 'wlpp-tray-view') return 'basket';
    if (el.name === 'checkout') return 'checkout';
    if (el.tagName !== 'A' || el.id === 'cart-icon-bubble') return null;
    let u; try { u = new URL(el.href, location.href); } catch (err) { return null; }
    if (u.origin !== location.origin) return null;
    return /^\/cart\/?$/.test(u.pathname) ? 'basket' : /\/checkouts?(\/|$)/.test(u.pathname) ? 'checkout' : null;
  }
  function toBasket(el) { return leavesFor(el) === 'basket'; }
  window.addEventListener('click', e => {
    const t = e.target.closest && e.target.closest('#wlpp-tray-view, [name="checkout"], a[href]');
    if (!t || !leavesFor(t) || !finish || finish.contains(t) || store.get(SHOWN) || !current.some(g => g.items.length)) return;
    e.preventDefault(); e.stopImmediatePropagation();
    openFinish(t);
  }, true);

  /* ---------- "Goes well with this paint" in the paint details ---------- */
  async function renderDetail(p) {
    const body = $('wlpp-dialog-body');
    if (!body) return;
    const slot = document.createElement('div');
    slot.className = 'wlpp-pair-detail';
    const guide = body.querySelector('.wlpp-guide-block');
    if (guide) guide.before(slot); else body.append(slot);
    let s = [];
    try { s = await suggest([p], 3, 1); } catch (e) {}
    if (!slot.isConnected) return;
    if (!s.length) { slot.remove(); return; }
    slot.innerHTML = `<h3>Goes well with this paint</h3><div class="wlpp-finish-list">${s[0].items.map(x => card(x, s[0].label)).join('')}</div>`;
  }

  /* ---------- Events ---------- */
  root.addEventListener('click', e => {
    const b = e.target.closest('button');
    if (!b || !root.contains(b) || finish.contains(b)) return;
    if (b.dataset.spAdd) { e.preventDefault(); add(b.dataset.spAdd); }
    else if (b.hasAttribute('data-sp-open')) openFinish(null);
  });
  let basketKey = '';
  function onBasket(d) {
    qty = d.qty || {}; basketPaints = d.paints || [];
    refreshButtons();
    // Only work the suggestions out again when the basket's contents change (the tray also re-renders on resize).
    const key = Object.keys(qty).sort().map(k => k + ':' + qty[k]).join(',');
    if (key !== basketKey) { basketKey = key; refreshSuggestions(); } else renderTray();
  }
  root.addEventListener('wlpp:basket', e => onBasket(e.detail));
  if (root.wlppBasket) onBasket(root.wlppBasket);
  root.addEventListener('wlpp:detail', e => renderDetail(e.detail));
})();
